import { Image as ImageIcon, Truck } from 'lucide-react';
import type { OfferSample, OfferSampleRow, SamplePaymentMode } from '../types';
import { EmptyRow, MicroLabel, RadioMark, cn } from '../ui/primitives';

/**
 * The three ways a sample can be paid for, and what each one means to a client
 * deciding whether to take it. The wording is the whole value of the control —
 * "deducted" and "charged" cost the same money on the day and settle differently.
 */
const PAYMENT_MODES: Array<{ key: SamplePaymentMode; label: string; hint: string }> = [
	{
		key: 'charged',
		label: 'Charged',
		hint: 'The client pays for the sample; it stays a cost on the order.',
	},
	{
		key: 'deducted',
		label: 'Deducted',
		hint: 'The client pays up front; it comes off the production invoice.',
	},
	{ key: 'waived', label: 'Waived', hint: 'The client is never charged for the sample.' },
];

const OPTION_ICON = { image: ImageIcon, truck: Truck } as const;

/**
 * How the client approves before the full run.
 *
 * Batch-level rather than one panel per product: the sample choice applies
 * whichever path the client takes through the offer, so it belongs beside the
 * quantity and delivery choices, not nested inside one of them.
 */
export function OfferSamples({
	rows,
	emptyLabel = 'No products on this offer.',
	noSampleLabel = 'No sample offered — the client approves on the full run.',
	noneOfferedLabel = 'No samples offered — the client approves on the full run.',
	showPayment = true,
}: {
	rows: OfferSampleRow[];
	emptyLabel?: string;
	/**
	 * What a product with no sample says, and what the section says when none of
	 * them has one.
	 *
	 * Words rather than a flag, because the two pages differ in who they are
	 * about: the admin reads a sentence about the client, and the client reads
	 * one about themselves. The defaults are the admin's, since it is the page
	 * that describes rather than addresses — the client card passes its own.
	 */
	noSampleLabel?: string;
	noneOfferedLabel?: string;
	/**
	 * Whether the Charged / Deducted / Waived strip is shown.
	 *
	 * It is an admin-facing distinction: how the sample settles against the
	 * production invoice is a pricing decision the client neither makes nor acts
	 * on here — what they owe is already in the totals. So the client card turns
	 * it off and the admin page keeps it.
	 */
	showPayment?: boolean;
}) {
	/* One line for the whole section rather than an empty row per product. A row
	   saying "no sample offered" is worth its height only when it stands beside
	   products that have one — on its own it is the section heading again, once
	   per product. Nothing is lost with it: the count is in the header, and so is
	   the control that adds the first one. */
	const noneOffered = rows.length > 0 && rows.every((row) => !row.sample);

	return (
		/* Rows in the card, not cards in the card. Every product used to sit in its
		   own bordered box, which put a second frame inside the section's own and
		   made this the only part of the offer that looked like a list of cards —
		   Products and Delivery separate their rows with a rule, so this does too.
		   The dashed box that marked a product with nothing offered goes with it:
		   the row says so in words, the way Delivery's "No method selected" does. */
		<div>
			{rows.length === 0 && (
				<div className="px-4 py-3">
					<EmptyRow>{emptyLabel}</EmptyRow>
				</div>
			)}
			{noneOffered && (
				<div className="px-4 py-3">
					<EmptyRow>{noneOfferedLabel}</EmptyRow>
				</div>
			)}
			{!noneOffered &&
				rows.map((row, i) => (
					<div key={row.id} className={cn(i > 0 && 'border-t border-line-1')}>
						<div className="flex flex-wrap items-center gap-2 px-4 py-3">
							<span className="min-w-[180px] flex-1">
								<span
									className={cn(
										'block truncate text-base font-medium',
										row.sample ? 'text-text-1' : 'text-text-3'
									)}
								>
									{row.product.name}
								</span>
								<span className="mt-px block text-xs text-text-4">
									{row.sample
										? `sample ${row.sample.production ?? '—'} · delivery ${row.sample.delivery ?? '—'}`
										: noSampleLabel}
								</span>
							</span>
							{showPayment && row.sample && <MicroLabel>Payment</MicroLabel>}
							{showPayment && row.sample && (
								<PaymentStrip payment={row.sample.payment} />
							)}
							{row.actions}
						</div>

						{/* Above the options, not below them: it qualifies the sample as a
						    whole, and the last thing before a set of choices reads as a
						    note about the last choice. No rule over it — it belongs to
						    the row above it, and a rule would cut the row in two. */}
						{row.sample?.notes && (
							<div className="px-4 pb-3 text-xs leading-relaxed text-text-3">
								{row.sample.notes}
							</div>
						)}

						{/* Only the last row's strip meets the card's rounded bottom —
						    see `atCardEdge`. */}
						{row.sample && (
							<SampleOptions
								row={row}
								sample={row.sample}
								atCardEdge={i === rows.length - 1}
							/>
						)}
					</div>
				))}
		</div>
	);
}

function PaymentStrip({ payment }: { payment: SamplePaymentMode }) {
	return (
		<span className="inline-flex overflow-hidden rounded-[var(--r-2)] border border-line-2">
			{PAYMENT_MODES.map((mode, i) => {
				const on = payment === mode.key;
				return (
					<span
						key={mode.key}
						title={mode.hint}
						className={cn(
							'px-3 py-1 text-sm whitespace-nowrap',
							i > 0 && 'border-l border-line-2',
							on ? 'bg-surface-fill font-medium text-text-1' : 'text-text-4'
						)}
					>
						{mode.label}
					</span>
				);
			})}
		</span>
	);
}

function SampleOptions({
	row,
	sample,
	atCardEdge,
}: {
	row: OfferSampleRow;
	sample: OfferSample;
	/**
	 * This strip is the last thing in the card, so its outer cells meet the
	 * card's rounded corners.
	 *
	 * The cells are square and run flush to the edge, and the selected one marks
	 * itself with an inset ring — so at the corner the card's `overflow-hidden`
	 * curve slices the ring off and leaves a tinted cell with a severed outline.
	 * Rounding the corner cells to the card's radius puts the ring back on the
	 * curve. Only for the last row: doing it to an interior strip would notch a
	 * corner in the middle of the card, where there is no curve to follow.
	 */
	atCardEdge: boolean;
}) {
	return (
		<div
			role={sample.onSelect ? 'radiogroup' : undefined}
			aria-label={sample.onSelect ? `Sample for ${row.product.name}` : undefined}
			className="grid gap-px border-t border-line-1 bg-line-1"
			style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}
		>
			{sample.options.map((option) => {
				const Icon =
					option.icon && option.icon !== 'none' ? OPTION_ICON[option.icon] : null;
				return (
					<button
						key={option.id}
						type="button"
						role="radio"
						aria-checked={option.selected}
						disabled={!sample.onSelect}
						onClick={() => sample.onSelect?.(option.id)}
						title={`${option.label} · ${option.price ?? '—'}`}
						className={cn(
							'flex min-w-0 items-center gap-2 px-3 py-3 text-left transition-colors',
							option.selected ? 'bg-accent-soft' : 'bg-surface-sunken',
							atCardEdge &&
								'first:rounded-bl-[var(--r-3)] last:rounded-br-[var(--r-3)]',
							sample.onSelect && !option.selected && 'hover:bg-surface-hover',
							!sample.onSelect && 'cursor-default'
						)}
						/* The same mark a picked delivery method carries: accent tint,
						   accent outline. This used to say "chosen" with a background a
						   shade lighter than its neighbours, which is a difference you
						   have to look for — and the choice sits directly under a row of
						   delivery buttons that shout it. Inset, because these cells sit
						   flush in a strip and have no border of their own to colour. */
						style={
							option.selected
								? { boxShadow: 'inset 0 0 0 1px var(--accent)' }
								: undefined
						}
					>
						<RadioMark on={option.selected} />
						<span
							className={cn(
								'flex min-w-0 flex-1 items-center gap-1.5 truncate text-sm',
								option.selected ? 'font-medium text-text-1' : 'text-text-3'
							)}
						>
							{Icon && <Icon size={12} className="shrink-0 text-text-4" />}
							{option.label}
						</span>
						<span
							className={cn(
								'shrink-0 font-mono text-sm whitespace-nowrap',
								option.selected ? 'text-text-1' : 'text-text-4'
							)}
						>
							{option.price ?? '—'}
						</span>
					</button>
				);
			})}
		</div>
	);
}
